import { useEffect, useMemo, useState } from "react"
import { FaFlag } from "react-icons/fa"
import {
  Box,
  CloseButton,
  Drawer,
  HStack,
  IconButton,
  Input,
  Portal,
  Stack,
} from "@chakra-ui/react"
import { Button, Text } from "@/components"
import { CompanyFlag, CompanyInfo } from "@/types"

const DEFAULT_FLAGS: CompanyFlag[] = [
  { label: "CEO Has Never Changed", code: "CEOChange", isOk: false, isMitigated: false },
  { label: "Last Round was Mostly Sold Out", code: "UnSoldRnd", isOk: false, isMitigated: false },
  { label: "Is Hiring Regularly", code: "NoHiring", isOk: false, isMitigated: false },
  { label: "Last VC Round Up", code: "LRPDown", isOk: false, isMitigated: false },
  { label: "No Layoff Stories Detected", code: "LayoffAlert", isOk: false, isMitigated: false },
  { label: "Employees Count UP Since Last Round", code: "EEDownLRP", isOk: false, isMitigated: false },
  { label: "Less Than 2 Years Since Last Round", code: "LRP>2yrsOld", isOk: false, isMitigated: false },
  { label: "At Least $10M in Funding", code: "Funding<10M", isOk: false, isMitigated: false },
  { label: "Cap Table Preference < 66% of Market Cap", code: "PrefOverHang", isOk: false, isMitigated: false },
  { label: "At Least Series B", code: "Series<B", isOk: false, isMitigated: false },
  { label: "At Least 50 Employees", code: "EE<50", isOk: false, isMitigated: false },
  { label: "Last Round Bigger Than Prior", code: "LRPSmaller", isOk: false, isMitigated: false },
  { label: "409A is Not Down", code: "409ADown", isOk: false, isMitigated: false },
  { label: "Less Than 10 Years Since Series A", code: "OldSerA", isOk: false, isMitigated: false },
  { label: "Located in the United States", code: "ForeignCo", isOk: false, isMitigated: false },
  { label: "Last Round Not Pay to Play", code: "LRPpay2play", isOk: false, isMitigated: false },
  { label: "Last Round Led by New Investor", code: "LRPInsiderLed", isOk: false, isMitigated: false },
  { label: "At Least One News Article within Past Year", code: "NoRecentNews", isOk: false, isMitigated: false },
  { label: "At Least One Co-Founder Remaining", code: "NoFounderLeft", isOk: false, isMitigated: false },
  { label: "409A Fresher than 1 yr & Reconfirmed", code: "409A2Old", isOk: false, isMitigated: false },
  { label: "409A Not Too Low or Falling Relative to LRP", code: "FMVLRP", isOk: false, isMitigated: false },
  { label: "Co. Has Raised New Round Since a Major Pivot?", code: "NoLRPsincePivot", isOk: false, isMitigated: false },
  { label: "No Surge in Inquires from this Company", code: "DealSurge", isOk: false, isMitigated: false },
  { label: "Co. Has at Least 1 Famous VC?", code: "NoFamousVC", isOk: false, isMitigated: false },
  { label: "VC-funded as opposed to Private Equity", code: "PEBacked", isOk: false, isMitigated: false },
  { label: "409A Value Is Not an Even Dollar Figure", code: "409AEven#", isOk: false, isMitigated: false },
  { label: "No Recent Executive Departure", code: "ExecDepart", isOk: false, isMitigated: false },
  { label: "Employment Headcount is not Falling", code: "EEDown", isOk: false, isMitigated: false },
  { label: "Company is Not in the Penalty Box", code: "PenaltyBox", isOk: false, isMitigated: false },
  { label: "The Company has Not been Recapitalized", code: "RecapFlag", isOk: false, isMitigated: false },
  { label: "No Full Ratchet Antidilution", code: "FullRatchet", isOk: false, isMitigated: false },
  { label: "Employee Growth Rate > 10%", code: "EE<10%", isOk: false, isMitigated: false },
  { label: "Client is NOT a Founder nor CxO nor GC", code: "FounderCXO", isOk: false, isMitigated: false },
  { label: "Last Round is Pari Passu", code: "SrLP", isOk: false, isMitigated: false },
  { label: "Last Round is Not Drip Round", code: "DripFlag", isOk: false, isMitigated: false },
  { label: "409A is Not an Assertion", code: "409AProof", isOk: false, isMitigated: false },
]

interface Props {
  companyInfo: CompanyInfo
}

export const CompanyFlags = ({ companyInfo }: Props) => {
  const [flags, setFlags] = useState<CompanyFlag[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [submitText, setSubmitText] = useState("")

  // Reset flags when companyInfo changes
  useEffect(() => {
    const resetFlags = DEFAULT_FLAGS.map((flag) => ({
      ...flag,
      isOk: false,
      isMitigated: false,
    }))

    // Check if any rumor with flagId 4 has a date within 30 days of today
    const now = new Date()
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

    const hasMitigatedRumor = (companyInfo.rumors || []).some((rumor) => {
      if (rumor.rumorFlagId !== 4) return false
      if (!rumor.date) return false
      const rumorDate = new Date(rumor.date)
      return rumorDate >= thirtyDaysAgo && rumorDate <= now
    })

    if (hasMitigatedRumor) {
      resetFlags.forEach((flag) => {
        flag.isMitigated = true
      })
    }

    setFlags(resetFlags)
    setExpandedIndex(null)
    setSubmitText("")
  }, [companyInfo])

  const redCount = useMemo(() => {
    return flags.filter((f) => !f.isOk && !f.isMitigated).length
  }, [flags])

  const getEntryBg = (flag: CompanyFlag) => {
    if (flag.isOk) return "row.green"
    if (flag.isMitigated) return "row.yellow"
    return "row.red"
  }

  const getEntryBorder = (flag: CompanyFlag) => {
    if (flag.isOk) return "border.green"
    if (flag.isMitigated) return "border.yellow"
    return "border.red"
  }

  const handleEntryClick = (index: number) => {
    const flag = flags[index]
    if (flag.isOk || flag.isMitigated) return
    if (expandedIndex === index) {
      setExpandedIndex(null)
      setSubmitText("")
    } else {
      setExpandedIndex(index)
      setSubmitText(`${flag.code}: `)
    }
  }

  const handleSubmit = () => {
    // Submit logic here — for now just close the form
    setExpandedIndex(null)
    setSubmitText("")
  }

  return (
    <>
      {/* Floating Action Button */}
      <Box position="relative">
        <IconButton
          aria-label="Company flags"
          size="lg"
          borderRadius={10}
          boxShadow="lg"
          bg="fab.bg"
          onClick={() => setIsOpen(true)}
        >
          <FaFlag color="fab.icon" />
        </IconButton>
        {redCount > 0 && (
          <Box
            position="absolute"
            top="-6px"
            right="-6px"
            bg="red.500"
            color="fab.icon"
            borderRadius="full"
            w="20px"
            h="20px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="xs"
            fontWeight="bold"
          >
            {redCount}
          </Box>
        )}
      </Box>

      {/* Flags Drawer */}
      <Drawer.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)} size="md" placement="end">
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Header>
                <Drawer.Title>{companyInfo.name} Flags</Drawer.Title>
                <Drawer.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Drawer.CloseTrigger>
              </Drawer.Header>
              <Drawer.Body>
                <Stack gap={2}>
                  {flags.map((flag, index) => (
                    <Box key={flag.code}>
                      <Box
                        p={3}
                        bg={getEntryBg(flag)}
                        border="1px solid"
                        borderColor={getEntryBorder(flag)}
                        borderRadius="md"
                        cursor={!flag.isOk && !flag.isMitigated ? "pointer" : "default"}
                        _hover={
                          !flag.isOk && !flag.isMitigated
                            ? { opacity: 0.8 }
                            : undefined
                        }
                        onClick={() => handleEntryClick(index)}
                      >
                        <HStack justifyContent="space-between">
                          <Text fontSize="sm" fontWeight="medium">
                            {flag.label}
                          </Text>
                          <Text fontSize="xs" color="text.muted">
                            {flag.isOk ? "OK" : flag.isMitigated ? "Mitigated" : "Flagged"}
                          </Text>
                        </HStack>
                      </Box>

                      {/* Inline submit form for red entries */}
                      {expandedIndex === index && !flag.isOk && !flag.isMitigated && (
                        <Box
                          mt={1}
                          p={3}
                          bg="gray.50"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                        >
                          <HStack mb={2}>
                            <Input
                              size="sm"
                              value={submitText}
                              onChange={(e) => setSubmitText(e.target.value)}
                              placeholder={`${flag.code}: `}
                            />
                            <Text fontSize="sm" whiteSpace="nowrap" color="text.secondary">
                              {new Date().toLocaleDateString()}
                            </Text>
                          </HStack>
                          <Button size="sm" colorScheme="blue" onClick={handleSubmit}>
                            Submit
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </>
  )
}
